import React, { useRef, useEffect, useState, useCallback, useImperativeHandle } from "react";
import i18n from "locale/i18n";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import {
  CREATE_BIOMETRIC_SUCCESS,
  VERIFY_BIOMETRIC_SUCCESS,
  BIOMETRIC_NOT_SUPPORTED,
  BIOMETRIC_CREATE_ID,
  verifyBiometric,
  signOut,
  isMobile,
  currentLockModeSelector,
  getDateAfterMins
} from "@kubera/common";

import PrimaryButton from "components/button/PrimaryButton";
import Logo from "components/labels/AppLogo";

import { BiometricComponentRefContext } from "./BiometricComponentExports";

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100vh;
  flex-direction: column;
  padding: 0 10px;
`;

const AppLogo = styled(Logo)`
  width: 101px;
  margin-top: 28px;
  transform: scale(1.1) translateY(-90px);
`;

const SignoutLink = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  text-decoration: underline;
  cursor: pointer;
  color: ${props => props.theme.sidebarColor};
`;

const ActionContainer = styled.div`
  position: absolute;
  bottom: 70px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  row-gap: 42px;
`;

const RetryUnlock = styled(PrimaryButton)`
  width: 260px;
  height: 50px;
  font-weight: 700;
  font-size: 15px;
`;

const isMobileDevice = isMobile();

const BiometricComponent = ({ children }) => {
  const dispatch = useDispatch();
  const componentRef = useRef();
  const lockMode = useSelector(currentLockModeSelector);

  const [isBiometricInProgress, setIsBiometricInProgress] = useState(false);
  const [isBiometricSuccess, setIsBiometricSuccess] = useState(true);

  const handleSignOut = () => {
    signOut(true);
  };

  const setLockCookieWithExpiry = (mode = lockMode) => {
    const expires = getDateAfterMins(parseInt(mode, 10));
    if (mode === "default" || mode === "immediately") {
      Cookies.remove("setting-lock");
      return;
    }

    Cookies.set("setting-lock", window.location.host, { expires: expires, sameSite: "Strict", secure: true });
  };

  const handleBiometricCheck = useCallback(async (mode = lockMode, isSettingChange = false) => {
    if (!isMobileDevice) return;

    if (!isSettingChange && (mode === "default" || (mode !== "immediately" && Cookies.get("setting-lock")))) {
      setLockCookieWithExpiry(mode);
      return;
    }
    setIsBiometricInProgress(!isSettingChange ? true : false);
    const biometricStoredId = localStorage.getItem(BIOMETRIC_CREATE_ID);

    // pass `biometricStoredId` as param to verifyBiometric if server verification present
    const biometricResponse = await dispatch(verifyBiometric(biometricStoredId)).catch(err => {
      localStorage.removeItem(BIOMETRIC_CREATE_ID);
      console.error("Biometric verification error:", err);
    });

    let biometricSuccess = false;
    switch (biometricResponse?.type) {
      case CREATE_BIOMETRIC_SUCCESS:
        biometricSuccess = true;
        localStorage.setItem(BIOMETRIC_CREATE_ID, biometricResponse.payload.credentialId);
        break;
      case VERIFY_BIOMETRIC_SUCCESS:
        biometricSuccess = true;
        // Check with server code goes here
        break;
      case BIOMETRIC_NOT_SUPPORTED:
        biometricSuccess = true;
        break;
      default:
        biometricSuccess = false;
        break;
    }
    if (biometricSuccess) {
      setLockCookieWithExpiry(mode);
    }
    setIsBiometricSuccess(isSettingChange ? true : biometricSuccess);
    setIsBiometricInProgress(false);

    return biometricSuccess;
  }, []);

  useEffect(() => {
    handleBiometricCheck();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleBiometricCheck(lockMode);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [lockMode]);

  useImperativeHandle(componentRef, () => ({
    handleBiometricCheck
  }));

  if (isMobileDevice && (!isBiometricSuccess || isBiometricInProgress)) {
    return (
      <Container>
        <AppLogo hasNoLink />
        <ActionContainer>
          <RetryUnlock title={i18n.t("unlockNow")} onClick={handleBiometricCheck} isDisabled={isBiometricInProgress} />
          <SignoutLink onClick={handleSignOut}>{i18n.t("signOut")}</SignoutLink>
        </ActionContainer>
      </Container>
    );
  }

  return <BiometricComponentRefContext.Provider value={componentRef}>{children}</BiometricComponentRefContext.Provider>;
};

export default BiometricComponent;
